import { Injectable } from '@angular/core';
import { NbToastrService } from '@nebular/theme';

import { API } from '@aws-amplify/api';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { UtilityService } from 'src/app/core/services/utility.service';
import {
  InsightSpinHide,
  LoadInsightInactiveSuccess,
  LoadInsightsInactive,
  LoadInsightsInactiveSuccess,
  LoadInsightsSuccess,
  LoadInsightSuccess,
  ManageInsight,
  ReloadInsights,
  SaveInsightComplete,
  SaveInsightFail,
  StoreListOfFailedJobs
} from 'src/app/features/insights/redux/insights.actions';
import { Insight } from 'src/app/models/insight';
import { APIS } from 'src/app/shared/constants/api-constants';
import { AmplifyServiceBuilder } from 'src/app/shared/utils/amplify.service.builder';
import { ENVIRONMENT } from 'src/environments/environment';

@Injectable({ providedIn: 'any' })
export class InsightService {
  public owner: string;
  public signedRequests: any;
  public allKeys: string[] = [];
  public selectedKeys: string[] = [];
  public insights_api = APIS.INSIGHTS;
  public jobs_api_failed = APIS.FAILED_JOBS;
  public jobs_api = APIS.INSIGHTS_JOBS;
  public individual_jobs_api = APIS.SINGLE_JOBS;
  public type = 'insights';

  constructor(private store: Store, private toastrService: NbToastrService, private utilityService: UtilityService) {
    setInterval(() => {
      this.loadInsightsAmplify(false, true);
    }, 300000);
  }

  //Reload is simple boolean true false. If it is called by ReloadInsight action it simply shows the user that it finished action.
  public loadInsightsAmplify(reload?, enabled?) {
    const requestData = AmplifyServiceBuilder(this.insights_api, false, null, {
      enabled
    });

    API.get(this.insights_api, `/${ENVIRONMENT.name}/registrations`, requestData)
      .then((response) => {
        if (reload) {
          this.toastrService.show(status, $localize`Fetching Insights Complete`, {
            status: 'success'
          });
        }
        if (!enabled) {
          this.store.dispatch(new LoadInsightsInactiveSuccess(response));
        } else {
          this.store.dispatch(new LoadInsightsSuccess(response));
        }
      })
      .catch((error) => {
        console.error(error);
        this.toastrService.show(status, $localize`Fetching Insights Failed`, {
          status: 'danger'
        });
      });
  }

  public loadInsightAmplify(id) {
    const requestData = AmplifyServiceBuilder(this.insights_api, false, null);

    API.get(this.insights_api, `/${ENVIRONMENT.name}/registrations/${id}`, requestData)
      .then((response) => {
        this.toastrService.show(status, $localize`Fetching Insight Complete`, {
          status: 'success'
        });
        if (!response.enabled) {
          this.store.dispatch(new LoadInsightInactiveSuccess(response));
        } else {
          this.store.dispatch(new LoadInsightSuccess(response));
        }
      })
      .catch((error) => {
        console.error(error);
        this.toastrService.show(status, $localize`Fetching Insight Failed`, {
          status: 'danger'
        });
        this.store.dispatch(new InsightSpinHide(false));
      });
  }

  //Activate is used when user wants to activate Campaign and Insight from View Campaign component
  public postInsightsAmplify(insight: Insight, activate?: boolean) {
    const requestData = AmplifyServiceBuilder(this.insights_api, false, insight);
    API.post(this.insights_api, `/${ENVIRONMENT.name}/registrations`, requestData)
      .then((response) => {
        if (activate) {
          this.store.dispatch(new ManageInsight(response));
          this.toastrService.show(status, $localize`Insight activation complete`, {
            status: 'success'
          });
        } else if (activate !== undefined) {
          this.store.dispatch(new ManageInsight(response));
          this.toastrService.show(status, $localize`Insight deactivation complete`, {
            status: 'success'
          });
        } else {
          this.store.dispatch(new SaveInsightComplete(response, insight));
          this.store.dispatch(LoadInsightsInactive);
          this.store.dispatch(ReloadInsights);
        }
      })
      .catch((error) => {
        console.error(error);
        this.store.dispatch(new SaveInsightFail(error));
      });
  }

  public deleteInsightsAmplify(insight: Insight) {
    const requestData = AmplifyServiceBuilder(this.insights_api, false, insight);
    API.del(this.insights_api, `/${ENVIRONMENT.name}/registrations`, requestData)
      .then((response) => {
        this.store.dispatch(new SaveInsightComplete(true, insight));
      })
      .catch((error) => {
        console.error(error);
        this.store.dispatch(new SaveInsightFail(error));
      });
  }

  public loadFailedJobsAmplify(reload?) {
    const MS_PER_DAY = 7 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const queryStringParameters = {
      start_date: now - MS_PER_DAY,
      end_date: now
    };
    const requestData = AmplifyServiceBuilder(this.jobs_api_failed, true, false, queryStringParameters);

    API.get(this.jobs_api_failed, `/${ENVIRONMENT.name}/jobs/get-failed-jobs`, requestData)
      .then((response) => {
        if (reload) {
          this.toastrService.show(status, $localize`Fetching Failed Jobs Complete`, {
            status: 'info'
          });
        }
        this.store.dispatch(new StoreListOfFailedJobs(response));
      })
      .catch((error) => {
        console.error(error);
        this.toastrService.show(status, $localize`Fetching Failed Jobs Failed`, {
          status: 'danger'
        });
      });
  }

  public runInsightJob(insight) {
    const requestData = AmplifyServiceBuilder(this.jobs_api, true);

    return new Observable<any>((observer) => {
      API.post(this.jobs_api, `/${ENVIRONMENT.name}/jobs/insights/` + insight.id, requestData)
        .then((response) => {
          observer.next(response);
          this.toastrService.show(status, $localize`Starting Insight ${insight.id} Success`, { status: 'info' });
          observer.complete();
        })
        .catch((error) => {
          console.error(error);
          const message = this.utilityService.convertErrorToMessage(error);
          this.toastrService.show(status, $localize`Starting Insight ${insight.id} Failed: ${message}`, {
            status: 'danger'
          });
        });
    });
  }

  public terminateInsightJob(job_id, terminating_reason) {
    const body = { reason: terminating_reason ? terminating_reason : 'User Cancelled' };
    const requestData = AmplifyServiceBuilder(this.individual_jobs_api, true, body);

    return new Observable<any>((observer) => {
      API.del(this.individual_jobs_api, `/${ENVIRONMENT.name}/jobs/` + job_id, requestData)
        .then((response) => {
          observer.next(response);
          observer.complete();
        })
        .catch((error) => {
          console.error(error);
          const message = this.utilityService.convertErrorToMessage(error);
          this.toastrService.show(status, $localize`Stopping Insight ${job_id} Failed: ${message}`, {
            status: 'danger'
          });
        });
    });
  }
}
